/* *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 *
 * Copyright 2026 - Koninklijk Nederlands Meteorologisch Instituut (KNMI)
 * Copyright 2026 - Finnish Meteorological Institute (FMI)
 * Copyright 2026 - The Norwegian Meteorological Institute (MET Norway)
 * */

import type { EDRInstance } from '../../types';
import { isValid, parseCustomDateString } from '../dateUtils';

/**
 * Shared model for relative reference-time selection ("Latest", "Latest -1", …).
 *
 * A selection is stored as a token `latest-<offset>` instead of a concrete
 * instance id, so it can be resolved against the freshest instance list every
 * time data is fetched. That is what makes "Latest" dynamic: when a new model
 * run is published, the same token resolves to the newer instance.
 */

const RELATIVE_REFERENCE_TIME_PATTERN = /^latest-(\d+)$/;

export const DEFAULT_MAX_RELATIVE_REFERENCE_TIME_OPTIONS = 5;

export const relativeReferenceTimeToken = (offset: number): string =>
  `latest-${offset}`;

export const isRelativeReferenceTime = (value?: string): boolean =>
  value !== undefined && RELATIVE_REFERENCE_TIME_PATTERN.test(value);

export const parseRelativeReferenceTimeOffset = (
  value?: string,
): number | null => {
  const match = value?.match(RELATIVE_REFERENCE_TIME_PATTERN);
  if (!match) {
    return null;
  }
  const offset = Number(match[1]);
  return Number.isInteger(offset) && offset >= 0 ? offset : null;
};

export const formatRelativeReferenceTimeLabel = (offset: number): string =>
  offset > 0 ? `Latest -${offset}` : 'Latest';

export const getValidInstancesNewestFirst = (
  instances: EDRInstance[],
): EDRInstance[] =>
  instances
    .filter((instance) => isValid(parseCustomDateString(instance.id)))
    .sort(
      (firstInstance, secondInstance) =>
        parseCustomDateString(secondInstance.id).getTime() -
        parseCustomDateString(firstInstance.id).getTime(),
    );

export interface RelativeReferenceTimeOption {
  token: string;
  label: string;
  offset: number;
  instanceId: string;
}

export const buildRelativeReferenceTimeOptions = (
  instances: EDRInstance[],
  maxOptions = DEFAULT_MAX_RELATIVE_REFERENCE_TIME_OPTIONS,
): RelativeReferenceTimeOption[] =>
  getValidInstancesNewestFirst(instances)
    .slice(0, maxOptions)
    .map((instance, offset) => ({
      token: relativeReferenceTimeToken(offset),
      label: formatRelativeReferenceTimeLabel(offset),
      offset,
      instanceId: instance.id,
    }));

/**
 * Resolve a relative reference-time token to a concrete instance against the
 * given (possibly just-refreshed) instance list. Returns null when the value is
 * not a relative token or when no instance exists at that offset.
 */
export const resolveReferenceTimeInstance = (
  instances: EDRInstance[],
  referenceTime?: string,
): EDRInstance | null => {
  const offset = parseRelativeReferenceTimeOffset(referenceTime);
  if (offset === null) {
    return null;
  }
  return getValidInstancesNewestFirst(instances)[offset] ?? null;
};

export const extendCollectionInstances = (
  instances: EDRInstance[],
  selectedInstanceId: string | undefined,
): EDRInstance[] =>
  instances.findIndex((instance) => instance.id === selectedInstanceId) ===
    -1 &&
  selectedInstanceId &&
  !isRelativeReferenceTime(selectedInstanceId)
    ? [...instances, { id: selectedInstanceId }]
    : instances;
